import {
  InfiniteData,
  useInfiniteQuery,
  UseInfiniteQueryResult,
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import {regionsApi} from '../api';
import {RegionsFilter, RegionWithMetaModel, CreateRegionDTO} from '../model';
import {useCallback, useMemo, useState} from 'react';
import {useNotifications} from '../../../shared';

type RegionsState = {
  getInfiniteRegions: UseInfiniteQueryResult<InfiniteData<RegionWithMetaModel>, Error>;
  filters: RegionsFilter;
  updateFilter: (newFilter: Partial<RegionsFilter>) => void;
  createRegionMutation: UseMutationResult<unknown, Error, CreateRegionDTO>;
};

const DEFAULT_PAGE_SIZE = 25;

export const useRegions = (initialFilter: RegionsFilter = {}): RegionsState => {
  const queryClient = useQueryClient();
  const [filters, setFilters] = useState<RegionsFilter>(() => ({
    pageSize: DEFAULT_PAGE_SIZE,
    ...initialFilter,
  }));
  const {createNotification} = useNotifications();

  const infiniteQueryFilters = useMemo(() => {
    const {after, ...rest} = filters as any;
    return rest;
  }, [filters]);

  const getInfiniteRegions = useInfiniteQuery<
    RegionWithMetaModel,
    Error,
    InfiniteData<RegionWithMetaModel>,
    ['regions', 'infinite', Omit<RegionsFilter, 'after'>],
    string | undefined
  >({
    queryKey: ['regions', 'infinite', infiniteQueryFilters],
    queryFn: ({pageParam}) => {
      return regionsApi.getRegions({...infiniteQueryFilters, pageSize: filters.pageSize, after: pageParam});
    },
    initialPageParam: undefined,
    getNextPageParam: (lastPage, _allPages, _lastPageParam) => {
      return lastPage.pageInfo.hasNextPage ? lastPage.pageInfo.endCursor : undefined;
    },
    staleTime: 60 * 1000,
  });

  const updateFilter = useCallback((newFilter: Partial<RegionsFilter>) => {
    setFilters((prev) => {
      return {
        ...prev,
        ...newFilter,
      };
    });
  }, []);

  const createRegionMutation = useMutation({
    mutationFn: regionsApi.createRegion,
    onSuccess: () => {
      queryClient.invalidateQueries({queryKey: ['regions', 'infinite'], refetchType: 'all'});
      createNotification({
        title: 'Регион успешно создан',
        name: 'createRegion',
        theme: 'success',
      });
    },
  });

  return {
    getInfiniteRegions,
    filters,
    updateFilter,
    createRegionMutation,
  };
};
